import React, { FunctionComponent, memo, SyntheticEvent } from 'react'

import { FetchResult } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { Button, ButtonSize, ButtonVariant } from '@/atoms/button'
import UnknownFileIcon from '@/atoms/icons/file.svg'
import File7zipIcon from '@/atoms/icons/file-7z.svg'
import FileCsvIcon from '@/atoms/icons/file-csv.svg'
import FileDocIcon from '@/atoms/icons/file-doc.svg'
import FileEmlIcon from '@/atoms/icons/file-eml.svg'
import FileESigncIcon from '@/atoms/icons/file-esign.svg'
import FileJpgIcon from '@/atoms/icons/file-jpg.svg'
import FileJsonIcon from '@/atoms/icons/file-json.svg'
import FileMsgIcon from '@/atoms/icons/file-msg.svg'
import FilePdfIcon from '@/atoms/icons/file-pdf.svg'
import FilePngIcon from '@/atoms/icons/file-png.svg'
import FileRarIcon from '@/atoms/icons/file-rar.svg'
import TxtFileIcon from '@/atoms/icons/file-txt.svg'
import FileXlsIcon from '@/atoms/icons/file-xls.svg'
import FileXmlIcon from '@/atoms/icons/file-xml.svg'
import FileZipIcon from '@/atoms/icons/file-zip.svg'
import { Box, Flex } from '@/atoms/layout'
import { InputSmallText } from '@/atoms/typography'
import { getFileExtension } from '@/helpers/file'
import { formatPercent } from '@/helpers/number'
import { Space } from '@/styles'

import { FileIconWrapper, FileName, FileNameWrapper } from './styled-uploaded-file'

interface Props {
  filename: string
  size?: number
  download?: (() => void) | (() => Promise<FetchResult>)
  preview?: (() => void) | (() => Promise<FetchResult>)
}

const FileIcons = {
  'png': FilePngIcon,
  'pdf': FilePdfIcon,
  'jpg': FileJpgIcon,
  'jpeg': FileJpgIcon,
  'xls': FileXlsIcon,
  'xlsx': FileXlsIcon,
  'csv': FileCsvIcon,
  'doc': FileDocIcon,
  'docx': FileDocIcon,
  'edoc': FileESigncIcon,
  'adoc': FileESigncIcon,
  'xml': FileXmlIcon,
  '7z': File7zipIcon,
  'rar': FileRarIcon,
  'zip': FileZipIcon,
  'eml': FileEmlIcon,
  'msg': FileMsgIcon,
  'txt': TxtFileIcon,
  'json': FileJsonIcon,
}

const UploadedFile: FunctionComponent<Props> = ({ filename, size, download, preview }) => {
  const { t } = useTranslation()
  const type = getFileExtension(filename)
  const FileIcon = FileIcons[type as keyof typeof FileIcons] || UnknownFileIcon
  const hasValidSize = Number.isInteger(size)
  const withActionButtons = !!download && !!preview
  const downloadable = !!download && !preview

  const handleDownload = (e: SyntheticEvent) => {
    if (downloadable) {
      e.stopPropagation()
      download()
    }
  }

  return (
    <Box style={{ overflow: 'hidden' }}>
      <Flex alignItems="center" title={filename} width={1}>
        <FileNameWrapper alignItems="center" onClick={handleDownload} $downloadable={downloadable}>
          <FileIconWrapper>
            <FileIcon width="20px" height="25px" />
          </FileIconWrapper>
          <FileName $hasValidSize={hasValidSize} $downloadable={downloadable}>
            {filename}
          </FileName>
        </FileNameWrapper>

        {hasValidSize && <InputSmallText>{`${formatPercent(size / 1024, { withSign: false })}KB`}</InputSmallText>}
      </Flex>
      {withActionButtons && (
        <Flex width={1}>
          <Button onClick={download} variant={ButtonVariant.FLAT} size={ButtonSize.SMALL}>
            {t('global.download_btn')}
          </Button>
          <Button onClick={preview} ml={Space.S16} variant={ButtonVariant.FLAT} size={ButtonSize.SMALL}>
            {t('global.preview_btn')}
          </Button>
        </Flex>
      )}
    </Box>
  )
}

export default memo(UploadedFile)
